import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDrag } from 'react-dnd';
import Typo from '../Typo';
import IconAsButton from '../IconAsButton';
import theme from '../theme';
import { toRgba } from '../theme/colors';
import FileThumbnail from '../FileThumbnail';
import { media } from '../../utils/media';

export const ItemTypes = {
  All: 'all',
}

const Content = styled.div`
  background-color: ${props => props.theme.colors.NEU_50};
  position: relative;
  transition: all .1s cubic-bezier(1, 0.06, 0.58, 1.2);
  cursor: ${props => props.$draggable ? 'grab' : 'pointer'};
  &:active {
    cursor: ${props => props.$draggable ? 'grabbing' : 'pointer'};
  }

  img {
    pointer-events: none;
  }
`;

const TextWrapper = styled.div`
  max-width:182px;

  p {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  ${media.lessThan('laptop')} {
    max-width:130px;
  }

  ${media.lessThan('phablet')} {
    max-width:120px;
  }
`;

const FileInfo = styled.div`
  width: 100%;
  position: absolute;
  left: 0;
  bottom: 0;
  padding: 5px 15px;
  background-color: ${toRgba(theme.colors.NEU_999, 0.5)};
  backdrop-filter: blur(7px);
  display: flex;
  align-items: center;
  justify-content: space-between;

  button {
    position: fixed;
    right: 0;
    top:0;
  }

  ${media.lessThan('tablet')} {
    padding: 3px;
  }
`;

// Inner component that handles drag - only rendered when draggable=true
const DraggableFileCard = ({ file, onDragEnd, onClick, showInfoButton, onInfoClick, ...props }) => {
  const { t } = useTranslation();
  const fileName = file.name.split(".");

  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.All,
    item: file,
    end: async (item, monitor) => {
      const target = monitor.getDropResult();
      if (item && target && onDragEnd) {
        onDragEnd(item, target);
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
      handlerId: monitor.getHandlerId(),
    }),
  }), [file, onDragEnd]);

  const opacity = isDragging ? 0.4 : 1;

  const handleClick = () => {
    if (onClick) {
      onClick(file);
    }
  };

  const handleInfoClick = () => {
    if (onInfoClick) {
      onInfoClick(file);
    }
  };

  return (
    <Content
      ref={drag}
      style={{ opacity }}
      $draggable
      onClick={handleClick}
      {...props}
    >
      <FileThumbnail file={file} />
      <FileInfo>
        <TextWrapper>
          <Typo.Info color={theme.colors.NEU_50}>{fileName[0]}</Typo.Info>
          <Typo.Info color={theme.colors.NEU_50}>{t('globals.type')}: {fileName[fileName.length - 1]}</Typo.Info>
        </TextWrapper>
        {showInfoButton && (
          <IconAsButton
            iconName='Info'
            tooltipLabel={t('media.fileDetails')}
            color={theme.colors.NEU_50}
            clickHandle={handleInfoClick}
          />
        )}
      </FileInfo>
    </Content>
  );
};

// Simple file card without drag functionality
const SimpleFileCard = ({ file, onClick, showInfoButton, onInfoClick, ...props }) => {
  const { t } = useTranslation();
  const fileName = file.name.split(".");

  const handleClick = () => {
    if (onClick) {
      onClick(file);
    }
  };

  const handleInfoClick = () => {
    if (onInfoClick) {
      onInfoClick(file);
    }
  };

  return (
    <Content
      $draggable={false}
      onClick={handleClick}
      {...props}
    >
      <FileThumbnail file={file} />
      <FileInfo>
        <TextWrapper>
          <Typo.Info color={theme.colors.NEU_50}>{fileName[0]}</Typo.Info>
          <Typo.Info color={theme.colors.NEU_50}>{t('globals.type')}: {fileName[fileName.length - 1]}</Typo.Info>
        </TextWrapper>
        {showInfoButton && (
          <IconAsButton
            iconName='Info'
            tooltipLabel={t('media.fileDetails')}
            color={theme.colors.NEU_50}
            clickHandle={handleInfoClick}
          />
        )}
      </FileInfo>
    </Content>
  );
};

// Main component that switches between draggable and non-draggable versions
const FileCard = ({
  file,
  draggable = false,
  onDragEnd,
  onClick,
  showInfoButton = false,
  onInfoClick,
  ...props
}) => {
  if (draggable) {
    return (
      <DraggableFileCard
        file={file}
        onDragEnd={onDragEnd}
        onClick={onClick}
        showInfoButton={showInfoButton}
        onInfoClick={onInfoClick}
        {...props}
      />
    );
  }

  return (
    <SimpleFileCard
      file={file}
      onClick={onClick}
      showInfoButton={showInfoButton}
      onInfoClick={onInfoClick}
      {...props}
    />
  );
};

export default FileCard;
